// Feed Client - 완전 자동 생성된 GraphQL Client
// GraphQL Code Generator가 모든 타입과 hooks를 자동 생성

import { GraphQLClient } from 'graphql-request';
import { QueryClient } from '@tanstack/react-query';
import { ACCESS_TOKEN_KEY, TOKEN_KEY, TOKEN_TYPE_KEY } from '@edusense/shared-constants';

// 자동 생성된 모든 타입과 hooks export
export * from '../feed_client_graphql_out/generated/graphql';

// 🔧 Resolve auth token from localStorage (same as social-client)
function resolveAuthHeaderFromStorage(): string {
  try {
    if (typeof window === 'undefined') return '';
    const ls = window.localStorage;
    const ss = window.sessionStorage;

    const tryKeys = [
      ACCESS_TOKEN_KEY,
      TOKEN_KEY,
      'accessToken',
      'edusense_token',
      'auth_token',
      'reward_accessToken',
    ].filter(Boolean) as string[];

    let token: string | null = null;
    for (const k of tryKeys) {
      token = (ls && ls.getItem(k)) || (ss && ss.getItem(k));
      if (token) break;
    }

    // Fallback: scan any key that ends with 'accessToken' (case-insensitive)
    if (!token && ls) {
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i) || '';
        if (key === 'auth_access_token') continue;
        if (key.toLowerCase().endsWith('accesstoken')) {
          const v = ls.getItem(key);
          if (v) { token = v; break; }
        }
      }
    }
    if (!token && ss) {
      for (let i = 0; i < ss.length; i++) {
        const key = ss.key(i) || '';
        if (key === 'auth_access_token') continue;
        if (key.toLowerCase().endsWith('accesstoken')) {
          const v = ss.getItem(key);
          if (v) { token = v; break; }
        }
      }
    }

    const tokenType = (ls && (ls.getItem(TOKEN_TYPE_KEY) || ls.getItem('token_type'))) || (ss && ss.getItem('token_type')) || 'Bearer';
    return token ? `${tokenType || 'Bearer'} ${token}` : '';
  } catch {
    return '';
  }
}

// GraphQL Client 설정 (requestMiddleware로 매 요청마다 토큰 주입)
const FEED_GRAPHQL_URL = (typeof window !== 'undefined' && (window as any).REACT_APP_FEED_GRAPHQL_URL)
  || 'https://api.ugot.uk/api/feed/graphql';

export const feedGraphQLClient = new GraphQLClient(
  FEED_GRAPHQL_URL,
  {
    requestMiddleware: async (request) => {
      try {
        const auth = resolveAuthHeaderFromStorage();

        console.log('🔍 [feed-client] Auth header:', {
          hasToken: !!auth,
          authHeaderLength: auth.length,
          finalAuth: auth ? auth.substring(0, 30) + '...' : 'none'
        });

        return {
          ...request,
          credentials: 'include',
          headers: {
            ...(request.headers || {}),
            ...(auth ? { Authorization: auth } : {}),
          },
        };
      } catch (e) {
        console.error('🚨 [feed-client] requestMiddleware error:', e);
        return request;
      }
    },
    responseMiddleware: async (response) => {
      try {
        if (response && typeof response === 'object' && 'status' in response) {
          console.log('📥 [feed-client] GraphQL response:', {
            status: (response as any).status,
            hasErrors: !!(response as any).errors?.length,
          });
        }
      } catch (e) {
        console.error('🚨 [feed-client] responseMiddleware error:', e);
      }
    }
  }
);

// React Query Client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5분
      gcTime: 1000 * 60 * 10, // 10분
      retry: 3,
      refetchOnWindowFocus: false,
    },
  },
});

// 사용 예제를 위한 타입 재정의
export type {
  FeedItem,
  Author,
  Engagement,
  FeedConnection,
  FeedEdge,
  CreatePostInput,
  ToggleLikeInput,
  GetRepliesQuery,
  GetRepliesQueryVariables,
  AddCommentMutationVariables,
  SharePostMutationVariables,
} from '../feed_client_graphql_out/generated/graphql';

// GraphQL Documents export
export {
  GetRepliesDocument,
  AddCommentDocument,
  SharePostDocument,
} from '../feed_client_graphql_out/generated/graphql';

// Tweet Mutations - GraphQL Code Generator가 생성한 hooks를 alias로 export
export {
  useCreateTweetMutation as useCreateTweet,
  useCreateTweetWithMediaMutation as useCreateTweetWithMedia,
  useUpdateTweetMutation as useUpdateTweet,
  useDeleteTweetMutation as useDeleteTweet,
  useToggleLikeMutation as useLikeTweet,
  useToggleLikeMutation as useUnlikeTweet,
  useSharePostMutation as useRetweet,
  useSharePostMutation as useUndoRetweet,
} from '../feed_client_graphql_out/generated/graphql';

// Feed Mutations (Relay) - 주석 처리 (react-relay 의존성 제거)
// export {
//   useToggleLike,
//   useCreatePost,
//   useAddComment,
//   useDeletePost,
// } from './hooks/useFeedMutations';